import { HOLDEM_TUNING, OPPONENTS, type HoldemState } from '../../engine/games/holdem';
import { holdemHandName, scoreSeven } from '../../engine/poker';
import type { Card } from '../../engine/cards';
import { money } from '../../format';
import { cardsIn, PlayingCard } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

const NAMES = ['You', ...OPPONENTS.map((o) => o.name)];
const STREETS = ['Preflop', 'Flop', 'Turn', 'River'];
/** A losing river lands and holds, then luck, then the new river. */
const LOST_MS = 1000;
const BANNER_MS = 1200;
/** At a showdown, each player's cards turn over in turn. */
const SHOW_MS = 700;
const END_HOLD_MS = 300;

export function HoldemTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const s = run.tables.holdem?.state as HoldemState | undefined;
  const anim = snapshot.anim;
  const luck = secondChanceFrame(snapshot);
  const lostRiver = luck ? cardsIn(luck.split('\n').find((l) => l.startsWith('River:')) ?? '')[0] ?? null : null;
  const luckEnd = lostRiver !== null ? LOST_MS + BANNER_MS : 0;
  const showdown = s?.phase === 'done' && s.result?.how === 'showdown';
  // Before the first hand there are no seats yet.
  const others = s?.seats.length ? [1, 2].filter((i) => !s.seats[i]!.folded) : [];
  const elapsed = useAnimClock(snapshot, actions, luckEnd + (showdown ? others.length * SHOW_MS : 0) + END_HOLD_MS);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, lostRiver !== null ? LOST_MS : null, () => ({ what: 'A river that beat you is dealt again.', saved: true }), setBurst);

  if (!s || s.phase === 'idle') {
    return (
      <>
        <Stage className="holdem-stage">
          <div className="dice-rules">
            <p><strong>Fixed-limit Hold'em against Mae (tight) and Sal (loose, bluffs).</strong></p>
            <p>Your bet is the big blind; a hand costs about four blinds. Bets are one blind before the turn, two after. The house takes {Math.round(HOLDEM_TUNING.rake * 100)}% of the pot once a flop is dealt.</p>
          </div>
        </Stage>
        <Controls snapshot={snapshot} actions={actions} />
      </>
    );
  }

  // The board during the animation: the losing river first, then the new one.
  let board: Card[] = s.board;
  if (anim && lostRiver !== null && elapsed < luckEnd) board = [...s.board.slice(0, 4), lostRiver];
  // Showdown: opponents' cards turn over one player at a time, after any luck.
  const shownAt = (i: number) => {
    if (!showdown) return false;
    if (!anim) return true;
    return elapsed >= luckEnd + (others.indexOf(i) + 1) * SHOW_MS;
  };
  const pot = s.seats.reduce((n, x) => n + x.contrib, 0);
  const me = s.seats[0]!;
  const result = s.result;
  const winners = !anim && result ? result.winners : [];

  const seat = (i: number) => {
    const x = s.seats[i]!;
    const face = shownAt(i);
    const turn = s.phase === 'betting' && s.turn === i;
    const handName = face && result?.scores[i] ? holdemHandName(result.scores[i]!) : null;
    return (
      <div key={i} className={`seat${x.folded ? ' out' : ''}${turn ? ' turn' : ''}${winners.includes(i) ? ' winner' : ''}`}>
        <div className="seat-name">
          {NAMES[i]}
          {s.button === i && <span className="dealer-btn" aria-label="Dealer button">D</span>}
          {turn && <span className="thinking"> is thinking…</span>}
        </div>
        <div className="cards small">
          {x.folded ? <span className="seat-out">Folded</span> : x.hole.map((c, k) => (face ? <PlayingCard key={k} card={c} /> : <PlayingCard key={k} hidden />))}
        </div>
        <div className="seat-foot">{handName ?? (x.street > 0 ? `In: ${money(x.street)}` : x.allIn ? 'All in' : '')}</div>
      </div>
    );
  };

  const hand = holdemHandName(scoreSeven([...me.hole, ...s.board]));
  let summary: string | undefined;
  let tone: 'win' | 'loss' | '' = '';
  if (s.phase === 'done' && result && !anim) {
    const net = s.returned - me.contrib;
    if (result.how === 'youFold') summary = `You fold. −${money(me.contrib)}`;
    else if (winners.includes(0) && winners.length > 1) summary = `Split pot: ${net >= 0 ? '+' : '−'}${money(Math.abs(net))}`;
    else if (net > 0) summary = `${s.lucky ? '★ ' : ''}You win the pot! +${money(net)}`;
    else summary = `${NAMES[winners[0]!]} wins. −${money(me.contrib)}`;
    tone = net > 0 ? 'win' : net < 0 ? 'loss' : '';
  }

  return (
    <>
      <Stage className="holdem-stage" onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={false} />
        <div className="seats">{seat(1)}{seat(2)}</div>
        <div className="board-area">
          <div className="board-head">
            <span>{STREETS[s.street]}</span>
            <span className="pot">Pot {money(pot)}</span>
          </div>
          <div className="cards board">
            {[0, 1, 2, 3, 4].map((k) => (board[k] !== undefined ? <PlayingCard key={`${k}-${board[k]}`} card={board[k]} lost={!!anim && k === 4 && lostRiver !== null && elapsed >= LOST_MS && elapsed < luckEnd} /> : <div key={k} className="pcard slot" aria-hidden="true" />))}
          </div>
          {s.log.length > 0 && <div className="action-log">{s.log.slice(-2).join(' · ')}</div>}
        </div>
        <div className={`seat you${winners.includes(0) ? ' winner' : ''}${s.phase === 'betting' && s.turn === 0 ? ' turn' : ''}`}>
          <div className="seat-name">
            You {s.button === 0 && <span className="dealer-btn" aria-label="Dealer button">D</span>}
            <span className="seat-stat">{hand}{s.phase === 'betting' ? ` · win ${Math.round(s.equity * 100)}%` : ''} · in {money(me.contrib)}</span>
          </div>
          <div className="cards">{me.folded ? <span className="seat-out">Folded</span> : me.hole.map((c, k) => <PlayingCard key={k} card={c} />)}</div>
        </div>
        <StatusLine snapshot={snapshot} result={summary} tone={tone} />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} primaryId="call" onAct={() => setBurst(null)} />
    </>
  );
}
